import {
   InputBase, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Select, Modal, CircularProgress
} from "@mui/material";
import React, { createElement, useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Drawer as MuiDrawer,
  AppBar as MuiAppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  Fullscreen as FullscreenIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useThemeMode } from "../../Themes/ThemeContextTemp";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../Redux/slices/authSlice";
import { CapitalizeName } from "../../../utils/capitalizeName";
import createInstance from "../../../api/axiosinstance";
import department from "../../../card icons/department.png";
import role from "../../../card icons/role.png";
import hire from "../../../card icons/hire.png";
import reject from "../../../card icons/reject.png";
import candidate from "../../../card icons/candidate.png";

// Components
import DepartmentManagement from "../DepartmentManagement";
import UserManagement from "../UserManagement";
import RoleManagement from "../RoleManagement";
import PageRightsAccess from "../PageRightAccess";
import CandidateResumeManagement from "../AddNewCandidate";
import CandidatePerformance from "../CandidatePerformance";
import JobDescriptionManagementList from "../JobDescriptionManagementList";
import OrganizationDashboard from "../OrganizationDashboard";
import ScheduleInterview from "../ScheduleInterview";
import ListInterview from "../ListInterview";
import ViewCandidate from "../ViewCandidate";
import JobDescription from "../JobDescription";
import AddNewCandidate from "../AddNewCandidate";
import UpcomingInterviews from "../UpcomingInterviews";
import CompletedInterviews from "../CompletedInterviews";
import CandidatesHiredRejected from "../CandidatesHiredRejected";
import logo from "../../../components/home/assets/images/logoAi.png";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

const drawerWidth = 300;

// ==================== STYLED COMPONENTS ====================
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

// Updated StyledDrawer to use customColors prop
const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "customColors",
})(({ theme, open, customColors = {} }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      background: customColors.secondary || "#fff",
      borderRight: `1px solid ${customColors.border || "#e0e0e0"}`,
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      background: customColors.secondary || "#fff",
      borderRight: `1px solid ${customColors.border || "#e0e0e0"}`,
    },
  }),
}));

// Updated StyledAppBar to use customColors prop
const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "customColors",
})(({ theme, open, customColors = {} }) => ({
  background: customColors.primary || "#fff",
  borderBottom: `1px solid ${customColors.border || "#e0e0e0"}`,
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// ==================== MAIN COMPONENT ====================
const OranizationLandingPage = ({ title }) => {
  const aiDishaApi = createInstance("DISHA");
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { COLORS, theme, toggleTheme } = useThemeMode();

  // State Management
  const [menudata, setMenudata] = useState("Dashboard");
  const [titleText, setTitleText] = useState("Organization Admin Dashboard");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(true);
  const [showCreateNewJob, setShowCreateNewJob] = useState(false);
  const [showCreateNewCandidate, setShowCreateNewCandidate] = useState(false);
  const [organizationDetails, setOrganizationDetails] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [selectedOrgMaster, setSelectedOrgMaster] = useState(null);
  const [showOrgMasterCards, setShowOrgMasterCards] = useState(true);
  const [showCandidateCards, setShowCandidateCards] = useState(true);

  const userName = CapitalizeName(user?.name || "");

  // Menu Configuration
  const menuItems = [
    { label: "Dashboard", icon: HomeIcon, key: "Dashboard" },
    { label: "Organization Master", icon: SettingsIcon, key: "Organization Master" },
    { label: "User Management", icon: PeopleIcon, key: "User Management" },
    { label: "Candidate Management", icon: PeopleIcon, key: "Candidate Management" },
  ];

  const organizationMasterOptions = [
    {
      title: "Role Management",
      image: role,
      key: "Role Management",
      subtitle: "Manage roles and permissions",
      badge: "Configuration",
      tag: "Access",
    },
    {
      title: "Department Management",
      image: department,
      key: "Department Management",
      subtitle: "Manage departments",
      badge: "Configuration",
      tag: "Structure",
    },
  ];

  const candidateManagementOptions = [
    {
      title: "View Candidate",
      image: candidate,
      key: "View Candidate",
      subtitle: "Browse all candidates",
      badge: "Candidate",
      tag: "HR",
    },
    {
      title: "Candidates Hired",
      image: hire,
      key: "Candidates Hired",
      subtitle: "View hired candidates",
      badge: "Candidate",
      tag: "Success",
    },
    {
      title: "Candidates Rejected",
      image: reject,
      key: "Candidates Rejected",
      subtitle: "View rejected candidates",
      badge: "Candidate",
      tag: "Status",
    },
  ];
  
  // Fetch Organization Details
  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("Fetching organization data for user:", user);

        const orgId = user?.organizationId;
        if (!orgId) {
          setError("No organization ID found in user data");
          return;
        }

        const response = await aiDishaApi.get(`/get_organization_by_id?org_id=${orgId}`);

        console.log("API Response status:", response.status);

        if (response.status === 200 && response.data) {
          const data = response.data;
          console.log("Full API Response:", data);

          const orgData = data.organization || data;
          console.log("Organization data received from API:", orgData);
          console.log("Logo URL from API:", orgData.organization_logo_url);
          setOrganizationDetails(orgData);
        } else {
          console.warn("API call failed, checking user data...");

          const fallbackData = {
            organization_id: user?.organizationId,
            organization_name: user?.organizationName,
            organization_logo_url: user?.organizationLogoUrl,
            organization_domain: user?.organizationDomain,
          };

          if (fallbackData.organization_id) {
            console.log("Using fallback data:", fallbackData);
            setOrganizationDetails(fallbackData);
          } else {
            const errorText = await response.text();
            console.error("API error response:", errorText);
            setError(`Failed to fetch organization data: ${response.status}`);
          }
        }
      } catch (error) {
        console.error("Network error fetching organization data:", error);
        setError(`Network error: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [user?.organizationId]);

  // Fix Linode URL
  const fixLinodeUrl = (url) => {
    if (!url) return null;

    if (url.includes("ai-codedisha.s3.in-maa-1.linodeobjects.com")) {
      return url.replace(
        "ai-codedisha.s3.in-maa-1.linodeobjects.com",
        "in-maa-1.linodeobjects.com/ai-codedisha"
      );
    }

    return url;
  };

  // Event Handlers
  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleListItemClick = (item) => {
    setMenudata(item);
    setTitleText(item);
    setShowCreateNewJob(false);
    setShowCreateNewCandidate(false);
    setSelectedOrgMaster(null);
    setShowOrgMasterCards(true);
    setShowCandidateCards(true);
  };

  const handleOrgMasterCardClick = (masterKey, masterTitle) => {
    setSelectedOrgMaster(masterKey);
    setMenudata(masterKey);
    setTitleText(masterTitle);
    setShowOrgMasterCards(false);
  };

  const handleBackFromOrgMaster = () => {
    setSelectedOrgMaster(null);
    setMenudata("Organization Master");
    setTitleText("Organization Master");
    setShowOrgMasterCards(true);
  };

  const handleCandidateCardClick = (candidateKey, candidateTitle) => {
    setMenudata(candidateKey);
    setTitleText(candidateTitle);
    setShowCandidateCards(false);
  };

  const handleBackFromCandidate = () => {
    setMenudata("Candidate Management");
    setTitleText("Candidate Management");
    setShowCandidateCards(true);
  };

  const handleOpenCreateNewJob = () => {
    setMenudata("Create New Job");
    setTitleText("Create New Job");
    setShowCreateNewJob(true);
  };

  const handleOpenCreateNewCandidate = () => {
    setMenudata("Add Candidate");
    setTitleText("Add Candidate");
    setShowCreateNewCandidate(true);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchor(null);
  };

  const handleLogOut = () => {
    handleProfileMenuClose();
    localStorage.clear();
    sessionStorage.clear();
    dispatch(logout());
    setTimeout(() => navigate("/"), 0);
  };

  const handleFullscreen = () => {
    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    ) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } else {
      const element = document.documentElement;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", background: COLORS.background, minHeight: "100vh" }}>
      {/* AppBar */}
      <StyledAppBar position="fixed" open={open} customColors={COLORS}>
        <Toolbar sx={{ minHeight: "80px !important", display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>

            {/* Back Button for Organization Master */}
            {selectedOrgMaster && (
              <Tooltip title="Go back to Organization Master" arrow>
              <IconButton
                onClick={handleBackFromOrgMaster}
                sx={{ color: COLORS.accent, mr: 1 }}
                // title="Go back"
              >
                <ArrowBackIcon />
              </IconButton>
              </Tooltip>
            )}

           {/* Back Button for Candidate Management */}
            {["View Candidate", "Candidates Hired", "Candidates Rejected"].includes(menudata) && (
              <Tooltip title="Go back to Candidate Management" arrow>
              <IconButton
                onClick={handleBackFromCandidate}
                sx={{ color: COLORS.accent, mr: 1 }}
              >
                <ArrowBackIcon />
              </IconButton>
              </Tooltip>
            )}
                      </Box>

          {/* Right Side Actions */}
          <Stack direction="row" spacing={2} sx={{ display: "flex", alignItems: "center" }}>
            <Toolbar sx={{ justifyContent: "flex-end" }}>
              <Tooltip title={`Switch to ${theme === "light" ? "Dark" : "Light"} Mode`} arrow>
              <IconButton
                onClick={toggleTheme}
                sx={{
                  color: COLORS.text,
                  "&:hover": { background: COLORS.hoverBg },
                }}
              >
                {theme === "light" ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
              </Tooltip>
            </Toolbar>

            <Tooltip title="Fullscreen Mode (F11)" arrow>
              <IconButton
                onClick={handleFullscreen}
                sx={{
                  color: COLORS.text,
                  "&:hover": { background: COLORS.hoverBg },
                }}
              >
                <FullscreenIcon />
              </IconButton>
            </Tooltip>


{/* Profile Button — Org Logo + Arrow */}
<Tooltip title="Account" placement="bottom">
  <Button
    onClick={handleProfileMenuOpen}
    sx={{
      display: { xs: "none", sm: "flex" },
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "2px",
      minWidth: "auto",
      padding: "4px 8px",
      borderRadius: "10px",
      color: COLORS.text,
      "&:hover": { background: COLORS.hoverBg },
    }}
  >
    {/* Org Logo or fallback */}
    {organizationDetails?.organization_logo_url ? (
      <Box
        component="img"
        src={fixLinodeUrl(organizationDetails.organization_logo_url)}
        alt="Org Logo"
        sx={{
          width: 100,
          height: 34,
          objectFit: "contain",
          borderRadius: "6px",
          background: "white",
          border: `1.5px solid ${Boolean(profileAnchor) ? COLORS.accent : COLORS.border}`,
          transition: "border-color 0.2s ease",
          padding: "2px",
        }}
      />
    ) : (
      <Avatar
        sx={{
          width: 34,
          height: 34,
          background: COLORS.accent,
          fontSize: "14px",
          fontWeight: 600,
          border: `1.5px solid ${Boolean(profileAnchor) ? COLORS.accent : "transparent"}`,
          transition: "border-color 0.2s ease",
        }}
      >
        {organizationDetails?.organization_name?.charAt(0)?.toUpperCase() || userName.charAt(0).toUpperCase()}
      </Avatar>
    )}

    {/* Small arrow below */}
    <Box
      sx={{
        width: 0,
        height: 0,
        borderLeft: "4px solid transparent",
        borderRight: "4px solid transparent",
        borderTop: `4px solid ${COLORS.textSecondary}`,
        transition: "transform 0.2s ease",
        transform: Boolean(profileAnchor) ? "rotate(180deg)" : "rotate(0deg)",
        mt: "1px",
      }}
    />
  </Button>
</Tooltip>

{/* Dropdown Menu */}
<Menu
  anchorEl={profileAnchor}
  open={Boolean(profileAnchor)}
  onClose={handleProfileMenuClose}
  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
  transformOrigin={{ vertical: "top", horizontal: "right" }}
  PaperProps={{
    elevation: 0,
    sx: {
      background: COLORS.primary,
      border: `1px solid ${COLORS.border}`,
      borderRadius: "12px",
      mt: 1.5,
      minWidth: 260,
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    },
  }}
>
  {/* Info Section — Org + User */}
  <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>

    {/* Org Row */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      {organizationDetails?.organization_logo_url ? (
        <Box
          component="img"
          src={fixLinodeUrl(organizationDetails.organization_logo_url)}
          alt="Org Logo"
          sx={{
            width: 38,
            height: 38,
            objectFit: "contain",
            borderRadius: "8px",
            background: "white",
            border: `1px solid ${COLORS.border}`,
            padding: "3px",
            flexShrink: 0,
          }}
        />
      ) : (
        <Avatar sx={{ width: 38, height: 38, background: COLORS.accent, borderRadius: "8px", fontSize: "16px", fontWeight: 600 }}>
          {organizationDetails?.organization_name?.charAt(0)?.toUpperCase() || "O"}
        </Avatar>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "11px", color: COLORS.textSecondary, lineHeight: 1.2 }}>
          Organization
        </Typography>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {organizationDetails?.organization_name || "—"}
        </Typography>
      </Box>
    </Box>

    {/* Divider */}
    <Divider sx={{ borderColor: COLORS.border, mb: 1.5 }} />

    {/* User Row */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Avatar sx={{ width: 38, height: 38, background: COLORS.accent, fontSize: "15px", fontWeight: 600, flexShrink: 0 }}>
        {userName.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {userName}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: COLORS.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {user?.email || "Organization Admin"}
        </Typography>
      </Box>
    </Box>

  </Box>

  <Divider sx={{ borderColor: COLORS.border }} />

  {/* Action Buttons */}
  <Box sx={{ py: 0.5 }}>

    <MenuItem
        disabled
      sx={{ fontSize: "13px", color: COLORS.textSecondary, padding: "10px 20px", gap: 1.5, opacity: 0.45 }}
    >
      <PersonOutlineIcon sx={{ fontSize: "17px" }} />
      Profile
      <Typography component="span" sx={{ ml: "auto", fontSize: "10px" }}>Soon</Typography>
    </MenuItem>

    <MenuItem
      disabled
      sx={{ fontSize: "13px", color: COLORS.textSecondary, padding: "10px 20px", gap: 1.5, opacity: 0.45 }}
    >
      <SettingsOutlinedIcon sx={{ fontSize: "17px" }} />
      Settings
      <Typography component="span" sx={{ ml: "auto", fontSize: "10px" }}>Soon</Typography>
    </MenuItem>

  </Box>

  <Divider sx={{ borderColor: COLORS.border }} />

  {/* Logout */}
  <MenuItem
    onClick={handleLogOut}
    sx={{
      fontSize: "13px",
      color: "#e53935",
      padding: "12px 20px",
      gap: 1.5,
      borderRadius: "0 0 12px 12px",
      "&:hover": { background: "rgba(229,57,53,0.06)" },
    }}
  >
    <LogoutIcon sx={{ fontSize: "17px" }} />
    Log out
  </MenuItem>
</Menu>
          </Stack>
        </Toolbar>
      </StyledAppBar>

      {/* Drawer */}
      <StyledDrawer variant="permanent" open={open} customColors={COLORS}>
        <DrawerHeader
          sx={{
            minHeight: "70px",
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            padding: 0,
          }}
        >
          {open && isAuthenticated && (
            <>
              {/* Organization Logo and Name Section */}
              <Box
                sx={{
                  backgroundColor: COLORS.cardBg || "white",
                  padding: "15px",
                  borderRadius: "8px",
                  margin: "10px",
                  textAlign: "center",
                  gap: "10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  borderBottom: `1px solid ${COLORS.border}`,

                }}
              >
                {organizationDetails?.organization_logo_url && (
                  <Box
                    component="img"
                    // src={fixLinodeUrl(organizationDetails?.organization_logo_url)}
                    src = {logo}
                    alt="Org Logo"
                    sx={{
                      background: "white",
                      maxWidth: "100%",     // 👈 important
                      height: "80px",
                      objectFit: "contain", // 👈 KEY CHANGE
                      padding:"8px",
                      borderRadius: "8px",
                    }}
                  />
                )}

                {/* {organizationDetails?.organization_name && (
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: COLORS.text,
                      maxWidth: "250px",
                      wordBreak: "break-word",
                      justifyContent: "center",
                      // overflow: "hidden",
                      // textOverflow: "ellipsis",
                      // whiteSpace: "nowrap",
                    }}
                  >
                    {organizationDetails?.organization_name}
                  </Typography>
                )} */}

                {/* <Tooltip title={`${user?.name || ""}`} placement="right"> */}
                  {/* <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: COLORS.text,
                      maxWidth: "180px",
                      wordBreak: "break-word",
                      justifyContent: "center",
                      // overflow: "hidden",
                      // textOverflow: "ellipsis",
                      // whiteSpace: "nowrap",
                    }}
                  >
                    Welcome {userName}
                  </Typography> */}
                {/* </Tooltip> */}
              </Box>

              {/* Navigation Title */}
              <Box sx={{ pr: 2, width: "100%" }}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: COLORS.accent,
                    textTransform: "uppercase",
                    letterSpacing: "1.5px",
                    mb: 1,
                    textAlign: "center",
                  }}
                >
                  Navigation
                </Typography>
              </Box>
            </>
          )}
        </DrawerHeader>

        {/* Navigation List */}
        <List sx={{ color: COLORS.text, flex: 1 }}>
          {menuItems.map((item) => (
            <ListItem key={item.key} disablePadding>
              <ListItemButton
                onClick={() => handleListItemClick(item.key)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  "&:hover": {
                    background: COLORS.hoverBg,
                    color: COLORS.accent,
                  },
                  color: menudata === item.key && !selectedOrgMaster ? COLORS.accent : COLORS.text,
                }}
              >
                <Tooltip title={item.label} placement="right" arrow>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                    color: "inherit",
                  }}
                >
                  <item.icon sx={{ fontSize: "22px" }} />
                </ListItemIcon>
                </Tooltip>
                <ListItemText
                  primary={item.label}
                  sx={{
                    opacity: open ? 1 : 0,
                    fontSize: "14px",
                    "& .MuiTypography-root": {
                      fontSize: "14px",
                      fontWeight: menudata === item.key && !selectedOrgMaster ? 600 : 500,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Bottom Divider */}
        <Divider sx={{ borderColor: COLORS.border }} />

        {/* Bottom Actions */}
        {open && (
          <Box
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Button
              onClick={handleLogOut}
              fullWidth
              sx={{
                color: COLORS.accent,
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "flex-start",
                textTransform: "none",
                fontSize: "14px",
                padding: "10px 12px",
                borderRadius: "6px",
                border: `1px solid ${COLORS.border}`,
                "&:hover": {
                  background: COLORS.hoverBg,
                  borderColor: COLORS.accent,
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: "18px" }} />
              Logout
            </Button>
          </Box>
        )}
      </StyledDrawer>
      

     {/* NEW: Persistent Sidebar Edge Tab */}
      <Box
        onClick={handleDrawerToggle}
        sx={{
          position: "fixed",
          top: "50%",
          left: open ? drawerWidth : { xs: 57, sm: 65 }, 
          transform: "translate(-50%, -50%)",
          zIndex: 1201, 
          backgroundColor: COLORS.background,
          border: `1px solid ${COLORS.border}`,
          borderRadius: "8px", 
          width: "24px", 
          height: "48px", 
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
          transition: "left 0.22s ease-out",
          "&:hover": { background: COLORS.hoverBg },
        }}
      >
        {open ? (
          <ChevronLeftIcon sx={{ fontSize: "18px", color: COLORS.accent }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: "18px", color: COLORS.accent }} />
        )}
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1,
          pt:1,
          minHeight: "100vh",
          background: COLORS.background,
          overflow: "auto",
          marginTop: "80px",
        }}
      >
        {/* Dashboard */}
        {menudata === "Dashboard" && <OrganizationDashboard />}

        {/* Organization Master Cards Grid */}
        {menudata === "Organization Master" && showOrgMasterCards && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: 3,
              mt: 2,
            }}
          >
            {organizationMasterOptions.map((option) => (
              <Card
                key={option.key}
                onClick={() => handleOrgMasterCardClick(option.key, option.title)}
                sx={{
                  minWidth: { xs: 260, sm: 300 },
                  borderRadius: "10px",
                  border: `1px solid ${COLORS.border}`,
                  boxShadow: "0 10px 20px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                  background: COLORS.cardBg,
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 16px 30px rgba(0,0,0,0.12)",
                  },
                }}
              >
                {/* Image content */}
                <Box p={1.5}>
                  <Box
                    sx={{
                      borderRadius: "8px",
                      bgcolor: COLORS.card_img_bg || "#f5f5f5",
                      overflow: "hidden",
                      height: 180,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={option.image}
                      alt={option.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>

                <CardContent>
                  {/* Badges */}
                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    {option.badge && (
                      <Chip
                        label={option.badge}
                        size="small"
                        sx={{
                          bgcolor: COLORS.button_red_bg,
                          color: COLORS.background,
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      />
                    )}

                    {option.tag && (
                      <Chip
                        label={option.tag}
                        size="small"
                        sx={{
                           background:COLORS.card_chip_bg,
                           color:COLORS.card_txt_color,
                          fontSize: "12px",
                        }}
                      />
                    )}
                  </Box>

                  {/* Title */}
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: COLORS.text,
                      mb: 1,
                    }}
                  >
                    {option.title}
                  </Typography>

                  {/* Subtitle */}
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: COLORS.textSecondary,
                      lineHeight: 1.4,
                    }}
                  >
                    {option.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Candidate Management Cards Grid */}
        {menudata === "Candidate Management" && showCandidateCards && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: 3,
              mt: 2,
            }}
          >
            {candidateManagementOptions.map((option) => (
              <Card
                key={option.key}
                onClick={() => handleCandidateCardClick(option.key, option.title)}
                sx={{
                  minWidth: { xs: 260, sm: 300 },
                  borderRadius: "10px",
                  border: `1px solid ${COLORS.border}`,
                  boxShadow: "0 10px 20px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                  background: COLORS.cardBg,
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 16px 30px rgba(0,0,0,0.12)",
                  },
                }}
              >
                {/* Image content */}
                <Box p={1.5}>
                  <Box
                    sx={{
                      borderRadius: "8px",
                      bgcolor: COLORS.card_img_bg || "#f5f5f5",
                      overflow: "hidden",
                      height: 180,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={option.image}
                      alt={option.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>

                <CardContent>
                  {/* Badges */}
                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    {option.badge && (
                      <Chip
                        label={option.badge}
                        size="small"
                        sx={{
                          bgcolor: COLORS.button_red_bg,
                          color: COLORS.background,
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      />
                    )}

                    {option.tag && (
                      <Chip
                        label={option.tag}
                        size="small"
                        sx={{
                          background:COLORS.card_chip_bg,
                          color:COLORS.card_txt_color,
                          fontSize: "12px",
                        }}
                      />
                    )}
                  </Box>

                  {/* Title */}
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: COLORS.text,
                      mb: 1,
                    }}
                  >
                    {option.title}
                  </Typography>

                  {/* Subtitle */}
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: COLORS.textSecondary,
                      lineHeight: 1.4,
                    }}
                  >
                    {option.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Organization Master Sub-pages */}
        {selectedOrgMaster === "Department Management" && <DepartmentManagement />}
        {selectedOrgMaster === "Role Management" && <RoleManagement />}

        {/* Candidate Management Sub-pages */}
        {menudata === "View Candidate" && (
          <ViewCandidate onOpenCandidate={handleOpenCreateNewCandidate} />
        )}
        {menudata === "Candidates Hired" && <CandidatesHiredRejected value="Hired" />}
        {menudata === "Candidates Rejected" && <CandidatesHiredRejected value="Rejected" />}

        {/* Other Pages */}
        {menudata === "User Management" && <UserManagement />}
        {menudata === "Page access rights" && <PageRightsAccess />}
        {menudata === "Create New Job" && <JobDescription />}
        {menudata === "View Existing Jobs" && (
          <JobDescriptionManagementList onOpenJobDescription={handleOpenCreateNewJob} />
        )}
        {menudata === "Schedule Interview" && <ScheduleInterview />}
        {menudata === "List Interview" && <ListInterview />}
        {menudata === "Upcoming Interviews" && <UpcomingInterviews />}
        {menudata === "Completed Interviews" && <CompletedInterviews />}
        {menudata === "Candidate Performance" && <CandidatePerformance />}

        {/* Modal Components */}
        {showCreateNewCandidate && <AddNewCandidate />}
        {showCreateNewJob && <JobDescription />}
      </Box>
    </Box>
  );
};

export default OranizationLandingPage;